import { ChangeEvent, useCallback, useRef } from 'react';
import { t, Trans } from '@lingui/macro';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Message from '@decisiv/ui-components/lib/components/Message';
import { H3, H4, P } from '@decisiv/ui-components/lib/components/Typography';

import Table from '@decisiv/iconix/lib/components/Table';
import Trash from '@decisiv/iconix/lib/components/Trash';
import Upload from '@decisiv/iconix/lib/components/Upload';

import { eventStopPropagation } from 'utils/eventStopPropagation';
import { formatFileSize } from 'pages/Management/Fleets/BulkAddAssetModal/utils/formatFileSize';

import {
  buildHeaderErrorMessage,
  useTemplateMismatch,
} from 'utils/csvHeaders/useTemplateMismatch';
import { type ResetTarget } from '../types';
import { FileCard, FileErrorRow, HiddenFileInput, Row } from '../styles';
import {
  MAX_ASSET_ROWS,
  type ResetDepotFileError,
} from '../utils/validateResetDepotFile';

import { findTemplateMismatch } from '../utils/parseResetDepotCsv';

interface UploadStepProps {
  uploadedFile: File | null;
  onFileChange: (file: File | null) => void;
  fileError: ResetDepotFileError | null;
  serverError: string | null;
  /** ResetTarget's discriminant only -- this step has no use for depot ids. */
  scope: ResetTarget['scope'];
}

export default function UploadStep({
  uploadedFile,
  onFileChange,
  fileError,
  serverError,
  scope,
}: UploadStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isAccountScope = scope === 'account';

  const bodyCopy = isAccountScope
    ? t`Provide the complete list of assets that should exist in this account. The CSV will be used to determine the final inventory state of each depot.`
    : t`Provide the complete list of assets that should exist in this depot. The CSV will be used to determine its final inventory state.`;

  const alertDescription = isAccountScope
    ? t`Any asset currently in your account that does not appear in this file will be identified as a removal candidate. Make sure this file contains your complete asset list before continuing.`
    : t`Any asset currently in this depot that does not appear in this file will be identified as a removal candidate. Make sure this file contains your complete asset list before continuing.`;

  const uploadFileSubtitle = isAccountScope
    ? t`Select a CSV containing the complete inventory for every depot in this account.`
    : t`Select a CSV containing the complete inventory for this depot.`;

  const mismatch = useTemplateMismatch(
    uploadedFile,
    fileError === 'INVALID_HEADERS',
    findTemplateMismatch,
  );
  const headerErrorMessage = buildHeaderErrorMessage(mismatch);

  const fileErrorMessage = fileError
    ? {
        INVALID_TYPE: t`Invalid file type. Please upload a .csv file.`,
        INVALID_CSV: t`We could not read this CSV. Please verify the format matches the template.`,
        INVALID_HEADERS: headerErrorMessage,
        TOO_LARGE: t`File exceeds the maximum size of 2 MB`,
        EMPTY: isAccountScope
          ? t`This file has no asset rows. A file with no rows would remove every asset from every depot in this account.`
          : t`This file has no asset rows. A file with no rows would remove every asset from this depot.`,
        TOO_MANY_ROWS: t`File exceeds the maximum of ${MAX_ASSET_ROWS.toLocaleString()} Assets`,
      }[fileError]
    : null;

  const handleOnFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      /* istanbul ignore if -- @preserve */
      if (!file) return;
      onFileChange(file);
    },
    [onFileChange],
  );

  const handleOnDeleteFile = useCallback(() => {
    onFileChange(null);
    /* istanbul ignore else -- @preserve */
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onFileChange]);

  const handleDownloadTemplateClick = useCallback(() => {
    window.location.assign(
      `${import.meta.env.VITE_BFF_API_URL}/assets/reset_depot_assets_template`,
    );
  }, []);

  const hasFileSizeError = fileError === 'TOO_LARGE';

  return (
    <Flex flexDirection="column">
      <H3>
        <Trans>List of Assets</Trans>
      </H3>
      <P size="small" shade={1} marginTop={0.5}>
        {bodyCopy}
      </P>

      <Flex marginTop={1} data-testid="reset-depot-upload-alert">
        <Alert
          intent="information"
          title={t`Review Reset Impact`}
          description={alertDescription}
        />
      </Flex>

      <H4 marginTop={2}>
        <Trans>Upload File</Trans>
      </H4>
      <P size="small" shade={1}>
        {uploadFileSubtitle}
      </P>

      {!uploadedFile && (
        <>
          <Row>
            <Button
              icon={Upload}
              text={t`Upload CSV File`}
              size="small"
              kind="secondary"
              type="button"
              onClick={() => fileInputRef.current?.click()}
            />
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              data-testid="reset-depot-file-input"
              onChange={handleOnFileChange}
              onClick={eventStopPropagation}
            />
            <Button
              icon={Table}
              text={t`Download CSV Template`}
              size="small"
              type="button"
              variant="ghost"
              onClick={handleDownloadTemplateClick}
            />
          </Row>

          <Flex alignItems="center" marginTop={0.5}>
            <Message intent="help" size="small">
              <Trans>
                Max file size 2 MB and/or 10,000 Assets. One VIN per row
                required.
              </Trans>
            </Message>
          </Flex>
        </>
      )}

      {uploadedFile && (
        <>
          <FileCard hasError={!!fileError}>
            <Flex flexDirection="column" flex={1}>
              <P size="small" weight="semibold">
                {uploadedFile.name}
              </P>
              <P
                size="small"
                shade={hasFileSizeError ? undefined : 1}
                color={hasFileSizeError ? 'danger' : undefined}
              >
                {formatFileSize(uploadedFile.size)}
              </P>
            </Flex>
            <Button
              icon={Trash}
              size="small"
              kind="secondary"
              variant="ghost"
              onClick={handleOnDeleteFile}
              aria-label={t`Remove file`}
            />
          </FileCard>

          {fileErrorMessage && (
            <FileErrorRow>
              <Message intent="warning" size="small">
                {fileErrorMessage}
              </Message>
            </FileErrorRow>
          )}

          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            data-testid="reset-depot-file-input"
            onChange={handleOnFileChange}
            onClick={eventStopPropagation}
          />
        </>
      )}

      {/* Plan-time rejections arrive as top-level GraphQL errors and are already
          user-facing sentences. Rewording them here would put two sources of
          truth on the same rule. */}
      {serverError && (
        <FileErrorRow>
          <Message intent="warning" size="small">
            {serverError}
          </Message>
        </FileErrorRow>
      )}
    </Flex>
  );
}
